import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { canAdminPlans } from "@/lib/permissions";
import { toast } from "sonner";
import {
  ArrowLeft, Save, Trash2, Plus, Crosshair, AlertTriangle,
  CheckCircle2, Compass, MapPin, X, RefreshCw, Eye, Maximize2, Minimize2,
} from "lucide-react";

// SC-3 — Sheet → Project Calibration Studio.
//
// Workflow:
//   1. Load the sheet raster.
//   2. List imported project control points in a right rail.
//   3. Operator clicks "Place" on a CP → cursor becomes a crosshair.
//   4. Operator clicks on the sheet image → (nx, ny) pair recorded (0..1).
//   5. When ≥3 pairs are placed, the page POSTs to the server, which
//      computes the affine transform + per-point residuals + RMS +
//      confidence (high/medium/low) and persists.
//   6. Confidence + residuals + status (calibrated / partial / not) shown
//      in plain English.
//
// Plain-English rule: the operator never sees "affine matrix" or matrix
// values. They see "This sheet is calibrated · HIGH confidence · 0.4 ft
// average error".

const CONF_STYLE = {
  high:   { bg: "rgba(34,197,94,0.10)",  border: "rgba(34,197,94,0.55)",  text: "rgb(34,197,94)",  label: "HIGH" },
  medium: { bg: "rgba(245,158,11,0.10)", border: "rgba(245,158,11,0.55)", text: "rgb(245,158,11)", label: "MEDIUM" },
  low:    { bg: "rgba(239,68,68,0.10)",  border: "rgba(239,68,68,0.55)",  text: "rgb(239,68,68)",  label: "LOW" },
};

const STATUS_STYLE = {
  not_calibrated: { bg: "rgba(239,68,68,0.10)", border: "rgba(239,68,68,0.45)", text: "rgb(239,68,68)" },
  partial:        { bg: "rgba(245,158,11,0.10)", border: "rgba(245,158,11,0.45)", text: "rgb(245,158,11)" },
  calibrated:     { bg: "rgba(34,197,94,0.10)", border: "rgba(34,197,94,0.45)", text: "rgb(34,197,94)" },
};

const TOKEN_KEY = "fop_bearer";

export default function CalibrationStudio() {
  const { sheetId } = useParams();
  const { user } = useAuth();
  const canWrite = canAdminPlans(user);
  const navigate = useNavigate();

  const [sheet, setSheet] = useState(null);
  const [controlPoints, setControlPoints] = useState([]);
  const [calibration, setCalibration] = useState(null);
  const [unitsLabel, setUnitsLabel] = useState("ft");
  const [pairs, setPairs] = useState([]);
  const [pendingCpId, setPendingCpId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loadingErr, setLoadingErr] = useState("");
  const [imgError, setImgError] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  const imgRef = useRef(null);
  const containerRef = useRef(null);

  const REACT_APP_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const token = typeof window !== "undefined" ? window.localStorage.getItem(TOKEN_KEY) : null;
  const rasterUrl = sheet && token
    ? `${REACT_APP_BACKEND_URL}/api/sheets/${sheetId}/raster?dpr=1&token=${encodeURIComponent(token)}`
    : null;

  // Load sheet + calibration + control points + units.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cal = await api.get(`/sheets/${sheetId}/project-calibration`).then(r => r.data);
        if (cancelled) return;
        setSheet(cal.sheet);
        setCalibration(cal);
        setPairs(cal.enriched_pairs || cal.pairs || []);
        const cps = await api.get(`/projects/${cal.sheet.project_id}/survey-control/control-points`).then(r => r.data);
        if (cancelled) return;
        setControlPoints(cps);
        const settings = await api.get(`/projects/${cal.sheet.project_id}/survey-control/settings`).then(r => r.data);
        if (!cancelled) {
          setUnitsLabel(settings?.labels?.horizontal_units || "ft");
        }
      } catch (e) {
        if (!cancelled) setLoadingErr(e?.response?.data?.detail || e.message);
      }
    })();
    return () => { cancelled = true; };
  }, [sheetId]);

  const placedCpIds = useMemo(() => new Set(pairs.map(p => p.control_point_id)), [pairs]);

  const handleImageClick = (e) => {
    if (!pendingCpId || !canWrite) return;
    const img = imgRef.current;
    if (!img) return;
    const rect = img.getBoundingClientRect();
    const nx = (e.clientX - rect.left) / rect.width;
    const ny = (e.clientY - rect.top) / rect.height;
    if (nx < 0 || nx > 1 || ny < 0 || ny > 1) return;
    const cp = controlPoints.find(c => c.id === pendingCpId);
    if (!cp) return;
    setPairs(prev => {
      const filtered = prev.filter(p => p.control_point_id !== pendingCpId);
      return [...filtered, {
        control_point_id: pendingCpId,
        nx, ny,
        residual: null,
        point_number: cp.point_number,
        point_name: cp.point_name,
        northing: cp.northing,
        easting: cp.easting,
      }];
    });
    setPendingCpId(null);
  };

  const removePair = (cpId) => {
    setPairs(prev => prev.filter(p => p.control_point_id !== cpId));
  };

  const startPlace = (cpId) => {
    if (!canWrite) return;
    setPendingCpId(cpId);
  };

  const computeAndSave = async () => {
    if (pairs.length < 3) {
      toast.error("Place at least 3 control points before computing");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        pairs: pairs.map(p => ({ control_point_id: p.control_point_id, nx: p.nx, ny: p.ny })),
      };
      const { data } = await api.put(`/sheets/${sheetId}/project-calibration`, payload);
      setCalibration(data);
      setPairs(data.enriched_pairs || data.pairs || []);
      const label = data.status === "calibrated"
        ? `Calibration saved · ${data.confidence?.toUpperCase()} confidence · RMS ${data.rms_residual?.toFixed(3)} ${unitsLabel}`
        : "Calibration saved";
      toast.success(label);
    } catch (e) {
      toast.error("Save failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const saveDraft = async () => {
    setSaving(true);
    try {
      const payload = {
        pairs: pairs.map(p => ({ control_point_id: p.control_point_id, nx: p.nx, ny: p.ny })),
      };
      const { data } = await api.put(`/sheets/${sheetId}/project-calibration`, payload);
      setCalibration(data);
      setPairs(data.enriched_pairs || data.pairs || []);
      toast.success(pairs.length >= 3 ? "Saved" : "Draft saved");
    } catch (e) {
      toast.error("Save failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const clearAll = async () => {
    if (!window.confirm("Clear all calibration pairs for this sheet?")) return;
    try {
      await api.delete(`/sheets/${sheetId}/project-calibration`);
      setCalibration({ ...calibration, status: "not_calibrated", confidence: null, rms_residual: null, pairs: [], enriched_pairs: [], status_label: "This sheet is not calibrated." });
      setPairs([]);
      toast.success("Calibration cleared");
    } catch (e) {
      toast.error("Clear failed: " + (e?.response?.data?.detail || e.message));
    }
  };

  if (loadingErr) {
    return (
      <div className="p-8 max-w-3xl mx-auto" data-testid="calibration-error">
        <div className="text-sm text-[var(--st-escalated)]">Unable to load calibration: {loadingErr}</div>
      </div>
    );
  }
  if (!sheet) {
    return <div className="p-8 font-mono text-xs text-[var(--fop-text-3)]">Loading calibration studio…</div>;
  }

  const status = calibration?.status || "not_calibrated";
  const statusStyle = STATUS_STYLE[status];
  const confStyle = calibration?.confidence ? CONF_STYLE[calibration.confidence] : null;

  return (
    <div className="p-4 max-w-[1800px] mx-auto" data-testid="calibration-studio">
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <Link to={`/sheet/${sheetId}`} className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-[var(--fop-text-3)] hover:text-[var(--fop-text)]" data-testid="back-to-sheet">
            <ArrowLeft size={12} /> Back to Sheet
          </Link>
          <span className="text-[var(--fop-text-3)] mx-1">·</span>
          <Link to={`/project/${sheet.project_id}/survey-control`} className="text-xs font-mono uppercase tracking-wider text-[var(--fop-text-3)] hover:text-[var(--fop-text)]" data-testid="back-to-survey">
            Survey Control
          </Link>
        </div>
        <button onClick={() => setShowHelp(s => !s)} className="btn btn-ghost text-xs" data-testid="toggle-help">
          {showHelp ? "Hide help" : "How to calibrate"}
        </button>
      </div>

      <div className="mb-3">
        <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-[var(--fop-text-3)]">
          Sheet {sheet.sheet_number}
        </div>
        <h1 className="font-display text-2xl text-[var(--fop-text)] leading-tight">
          {sheet.sheet_title} <span className="text-[var(--fop-text-3)] text-base">· Project Calibration</span>
        </h1>
      </div>

      <StatusStrip status={status} statusStyle={statusStyle} confStyle={confStyle}
                    calibration={calibration} unitsLabel={unitsLabel} pairs={pairs} />

      {showHelp && <HelpPanel />}

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-3 mt-3">
        {/* CANVAS */}
        <div ref={containerRef} className="fop-surface-2 rounded-lg p-2 min-h-[400px] relative"
              data-testid="calibration-canvas-wrap">
          {imgError && (
            <div className="absolute inset-0 flex items-center justify-center text-sm text-[var(--st-escalated)] z-10" data-testid="canvas-img-error">
              Could not load sheet image
            </div>
          )}
          {/* Constrain the sheet to the visible viewport so ny≈0.95 clicks
              are reachable without scroll on 1080p laptops. Reported by the
              SC-3 design audit. */}
          <div className="relative mx-auto"
                style={{
                  cursor: pendingCpId ? "crosshair" : "default",
                  maxHeight: "calc(100vh - 280px)",
                  maxWidth: "100%",
                  overflow: "auto",
                }}>
            {rasterUrl && (
              <img
                ref={imgRef}
                src={rasterUrl}
                alt={`${sheet.sheet_number} ${sheet.sheet_title}`}
                onClick={handleImageClick}
                onError={() => setImgError(true)}
                onLoad={() => setImgError(false)}
                className="h-auto rounded select-none"
                style={{ background: "#fff", maxHeight: "calc(100vh - 296px)", maxWidth: "100%", width: "auto", display: "block" }}
                draggable={false}
                data-testid="calibration-sheet-img"
              />
            )}
            {/* Pair markers */}
            {pairs.map(p => (
              <div key={p.control_point_id}
                    className="absolute pointer-events-none"
                    style={{
                      left: `${p.nx * 100}%`,
                      top: `${p.ny * 100}%`,
                      transform: "translate(-50%, -50%)",
                    }}
                    data-testid={`pair-marker-${p.point_number || p.control_point_id}`}>
                <div className="relative">
                  <div className="w-4 h-4 rounded-full border-2 border-[var(--fop-primary)] bg-white" />
                  <div className="absolute top-1/2 left-1/2 w-1 h-1 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[var(--fop-primary)]" />
                  <div className="absolute left-5 top-[-4px] whitespace-nowrap px-1.5 py-0.5 rounded text-[10px] font-mono bg-[var(--fop-cta-bg)] text-white">
                    {p.point_number}
                    {p.residual != null && (
                      <span className="ml-1 opacity-80">{p.residual.toFixed(2)}{unitsLabel === "Meters" ? "m" : "ft"}</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {pendingCpId && (
              <div className="absolute top-2 left-2 px-2 py-1 rounded bg-[var(--fop-cta-bg)] text-white text-[11px] font-mono uppercase tracking-wider pointer-events-none"
                    data-testid="placing-hint">
                <Crosshair size={11} className="inline mr-1" />
                Click sheet to place {controlPoints.find(c => c.id === pendingCpId)?.point_number}
              </div>
            )}
          </div>
        </div>

        {/* RIGHT RAIL — control points list + actions */}
        <div className="space-y-3">
          <ControlPointsRail
            controlPoints={controlPoints}
            placedCpIds={placedCpIds}
            pendingCpId={pendingCpId}
            pairs={pairs}
            canWrite={canWrite}
            unitsLabel={unitsLabel}
            onStartPlace={startPlace}
            onCancelPlace={() => setPendingCpId(null)}
            onRemovePair={removePair}
            projectId={sheet.project_id}
          />
          {canWrite && (
            <div className="fop-surface-2 rounded-lg p-3 space-y-2" data-testid="calibration-actions">
              <button onClick={computeAndSave}
                      disabled={saving || pairs.length < 3}
                      className="btn btn-primary text-sm w-full inline-flex items-center justify-center gap-1.5"
                      data-testid="compute-calibration-btn">
                <RefreshCw size={13} /> {saving ? "Saving…" : (pairs.length < 3 ? `Need ${3 - pairs.length} more point${pairs.length === 2 ? "" : "s"}` : "Compute & Save")}
              </button>
              <button onClick={saveDraft}
                      disabled={saving || pairs.length === 0}
                      className="btn btn-ghost text-xs w-full"
                      data-testid="save-draft-btn">
                Save draft
              </button>
              <button onClick={clearAll}
                      disabled={saving || pairs.length === 0}
                      className="btn btn-ghost text-xs w-full text-[var(--st-escalated)]"
                      data-testid="clear-all-btn">
                <Trash2 size={11} className="inline mr-1" /> Clear all pairs
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


function StatusStrip({ status, statusStyle, confStyle, calibration, unitsLabel, pairs }) {
  const friendly = calibration?.status_label || "This sheet is not calibrated.";
  return (
    <div className="rounded-lg p-3 border-2 flex items-center gap-4 flex-wrap"
          style={{ background: statusStyle.bg, borderColor: statusStyle.border }}
          data-testid="status-strip">
      <div className="flex items-center gap-2 min-w-0">
        <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: statusStyle.text }} data-testid="status-dot" />
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em]" style={{ color: statusStyle.text }} data-testid="status-label">
            {status.replace("_", " ").toUpperCase()}
          </div>
          <div className="text-sm text-[var(--fop-text)]" data-testid="status-friendly">{friendly}</div>
        </div>
      </div>

      {status === "calibrated" && confStyle && (
        <div className="flex items-center gap-3" data-testid="confidence-block">
          <span className="font-mono text-[10px] uppercase tracking-wider text-[var(--fop-text-3)]">Confidence</span>
          <span className="px-2 py-0.5 rounded font-mono text-[11px] uppercase tracking-wider"
                  style={{ background: confStyle.bg, color: confStyle.text, border: `1px solid ${confStyle.border}` }}
                  data-testid="confidence-pill">
            {confStyle.label}
          </span>
          {calibration.rms_residual != null && (
            <span className="font-mono text-xs text-[var(--fop-text-2)]" data-testid="rms-residual">
              RMS {calibration.rms_residual.toFixed(3)} {unitsLabel}
            </span>
          )}
        </div>
      )}

      <div className="ml-auto font-mono text-[11px] uppercase tracking-wider text-[var(--fop-text-3)]" data-testid="pairs-count">
        {pairs.length} of 3+ points placed
      </div>
    </div>
  );
}


function HelpPanel() {
  return (
    <div className="fop-surface-2 rounded-lg p-4 mt-3 text-sm text-[var(--fop-text-2)]" data-testid="help-panel">
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--fop-text-3)] mb-1">How to calibrate this sheet</div>
      <ol className="list-decimal pl-5 space-y-1">
        <li>Make sure your project has at least 3 imported control points (right rail lists them).</li>
        <li>Find the same point on the sheet (typically a labeled control monument or property corner).</li>
        <li>Click <b>Place</b> next to a control point in the right rail — your cursor turns into a crosshair.</li>
        <li>Click that point on the sheet. The marker stays put. Repeat for at least 3 points spread across the sheet.</li>
        <li>Hit <b>Compute &amp; Save</b>. The platform calculates the transform and shows confidence + average error.</li>
        <li>If a point shows a big residual, click it to remove and place it again more accurately.</li>
      </ol>
    </div>
  );
}


function ControlPointsRail({
  controlPoints, placedCpIds, pendingCpId, pairs, canWrite, unitsLabel,
  onStartPlace, onCancelPlace, onRemovePair, projectId,
}) {
  if (controlPoints.length === 0) {
    return (
      <div className="fop-surface-2 rounded-lg p-4" data-testid="cp-rail-empty">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--fop-text-3)] mb-1">Control points missing</div>
        <p className="text-sm text-[var(--fop-text)] mb-3">
          This project doesn&apos;t have any control points yet. Import or add them in Survey Control before calibrating sheets.
        </p>
        <Link to={`/project/${projectId}/survey-control`} className="btn btn-primary text-xs inline-flex items-center gap-1.5" data-testid="go-add-cps">
          <Plus size={12} /> Open Survey Control
        </Link>
      </div>
    );
  }
  return (
    <div className="fop-surface-2 rounded-lg" data-testid="cp-rail">
      <div className="p-3 border-b border-[var(--fop-border)] flex items-center justify-between">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--fop-text-3)]">Project Control Points</div>
          <div className="text-xs text-[var(--fop-text-2)]">
            {placedCpIds.size} of {controlPoints.length} placed · need 3+
          </div>
        </div>
        {pendingCpId && (
          <button onClick={onCancelPlace} className="text-[var(--fop-text-3)] hover:text-[var(--fop-text)]" data-testid="cancel-place">
            <X size={14} />
          </button>
        )}
      </div>
      <div className="max-h-[60vh] overflow-y-auto">
        {controlPoints.map(cp => {
          const placed = placedCpIds.has(cp.id);
          const pair = pairs.find(p => p.control_point_id === cp.id);
          const isPending = pendingCpId === cp.id;
          return (
            <div key={cp.id}
                  className={`p-3 border-b border-[var(--fop-border)] ${isPending ? "bg-[var(--fop-primary-soft)]" : ""}`}
                  data-testid={`cp-rail-row-${cp.point_number}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <span className="font-mono text-sm text-[var(--fop-text)]">{cp.point_number}</span>
                    {cp.point_name && <span className="text-xs text-[var(--fop-text-3)] truncate">· {cp.point_name}</span>}
                  </div>
                  <div className="font-mono text-[10px] text-[var(--fop-text-3)] truncate">
                    N {cp.northing?.toFixed(2)} · E {cp.easting?.toFixed(2)}
                  </div>
                  {placed && pair?.residual != null && (
                    <div className="font-mono text-[10px] mt-0.5" style={{ color: pair.residual > 2 ? "rgb(245,158,11)" : "rgb(34,197,94)" }}
                          data-testid={`cp-rail-residual-${cp.point_number}`}>
                      Error {pair.residual.toFixed(3)} {unitsLabel}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {placed ? (
                    <>
                      <span className="px-1.5 py-0.5 rounded text-[9px] font-mono uppercase tracking-wider bg-[rgba(34,197,94,0.15)] text-[rgb(34,197,94)]" data-testid={`cp-rail-placed-${cp.point_number}`}>
                        Placed
                      </span>
                      {canWrite && (
                        <button onClick={() => onRemovePair(cp.id)} className="text-[var(--fop-text-3)] hover:text-[var(--st-escalated)]" title="Remove" data-testid={`cp-rail-remove-${cp.point_number}`}>
                          <Trash2 size={12} />
                        </button>
                      )}
                    </>
                  ) : canWrite ? (
                    <button onClick={() => onStartPlace(cp.id)}
                            className="btn btn-ghost text-[10px] px-2 py-1 inline-flex items-center gap-1"
                            data-testid={`cp-rail-place-${cp.point_number}`}>
                      <Crosshair size={10} /> Place
                    </button>
                  ) : null}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
